import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Building2, Database, Eye, EyeOff, KeyRound, RefreshCw, Save, Search, ShieldCheck, Trash2, UserCheck, UserPlus, Users, UserX, X } from "lucide-react";

import { useAuth } from "../auth/useAuth";
import { LandingShell } from "../components/LandingShell";
import { useAdminUsers } from "../hooks/useAdminUsers";
import { ApiHttpError, apiJson } from "../lib/api";
import type { AdminUser, AdminUserActionResult, AdminUserSummary, CreateAdminUserInput } from "../types/adminUsers";

type BlulogAccessUser = AdminUser & {
  can_view_blulog: boolean;
};

type AdminClient = {
  id: string;
  name: string;
  created_at: string;
};

type ClientAccess = {
  client_id: string;
  client_name: string;
  role: "viewer" | "admin";
};

type DatasetAccess = {
  dataset_key: string;
  dataset_name: string | null;
  client_id: string | null;
  client_name: string | null;
  can_view: boolean;
  can_admin: boolean;
};

type AccessResponse = {
  user: AdminUser;
  clientAccess: ClientAccess[];
  datasetAccess: DatasetAccess[];
};

type AdminView = "datasets" | "blulog";
type DatasetRole = "none" | "viewer" | "admin";

export function AdminAccessPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("datavis_admin") ?? false;

  if (!isAdmin) {
    return (
      <LandingShell active="configuration" breadcrumb="Accès refusé">
        <div className="admin-access-feedback is-error" role="alert">
          <ShieldCheck size={20} aria-hidden="true" />
          <span>Erreur 403 : accès administrateur requis.</span>
        </div>
      </LandingShell>
    );
  }

  return <AdminAccessContent />;
}

function AdminAccessContent() {
  const { login } = useAuth();
  const [activeView, setActiveView] = useState<AdminView>("datasets");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [blulogUsers, setBlulogUsers] = useState<BlulogAccessUser[]>([]);
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedKeycloakUserId, setSelectedKeycloakUserId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [access, setAccess] = useState<AccessResponse | null>(null);
  const [savedAccess, setSavedAccess] = useState<AccessResponse | null>(null);
  const [userFilter, setUserFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccessLoading, setIsAccessLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [savingBlulogUsers, setSavingBlulogUsers] = useState<Set<string>>(new Set());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState<AdminUserSummary | null>(null);
  const [statusChange, setStatusChange] = useState<{ user: AdminUserSummary; enabled: boolean } | null>(null);
  const [deletionUser, setDeletionUser] = useState<AdminUserSummary | null>(null);
  const adminUsers = useAdminUsers();

  useEffect(() => {
    void loadAdminData();
  }, []);

  useEffect(() => {
    if (!selectedUserId) {
      setAccess(null);
      setSavedAccess(null);
      return;
    }

    void loadUserAccess(selectedUserId);
  }, [selectedUserId]);

  const visibleLocalUsers = useMemo(() => {
    const filter = userFilter.trim().toLowerCase();
    if (!filter) return users;
    return users.filter((item) => userLabel(item).toLowerCase().includes(filter));
  }, [userFilter, users]);

  const visibleClients = useMemo(() => {
    const filter = clientFilter.trim().toLowerCase();
    if (!filter) return clients;

    return clients.filter((client) => {
      if (client.name.toLowerCase().includes(filter)) return true;
      return (access?.datasetAccess ?? []).some((dataset) => {
        if (dataset.client_id !== client.id) return false;
        const label = `${dataset.dataset_name ?? ""} ${dataset.dataset_key}`;
        return label.toLowerCase().includes(filter);
      });
    });
  }, [access, clientFilter, clients]);

  const selectedUser = access?.user ?? users.find((item) => item.id === selectedUserId) ?? null;
  const selectedIdentity = adminUsers.users.find((item) => item.keycloakId === selectedKeycloakUserId)
    ?? (selectedUserId ? adminUsers.users.find((item) => item.localUserId === selectedUserId) : null)
    ?? null;
  const canEditAcl = Boolean(selectedUserId && selectedIdentity?.provisioningStatus !== "not_provisioned");
  const selectedClient = clients.find((client) => client.id === selectedClientId) ?? null;
  const selectedDatasets = useMemo(
    () => (access?.datasetAccess ?? []).filter((dataset) => dataset.client_id === selectedClientId),
    [access, selectedClientId],
  );
  const dirtyDatasetKeys = useMemo(() => {
    const keys = new Set<string>();
    if (!access || !savedAccess) return keys;

    for (const dataset of access.datasetAccess) {
      const saved = savedAccess.datasetAccess.find((item) => item.dataset_key === dataset.dataset_key);
      if (!saved || !samePermissions(dataset, saved)) keys.add(dataset.dataset_key);
    }
    return keys;
  }, [access, savedAccess]);
  const dirtySelectedDatasets = selectedDatasets.filter((dataset) => dirtyDatasetKeys.has(dataset.dataset_key));

  async function loadAdminData(refresh = false, preferredUserId?: string) {
    if (refresh) setIsRefreshing(true);
    if (!refresh) setIsLoading(true);
    setError(null);

    try {
      const [usersResponse, clientsResponse, blulogResponse] = await Promise.all([
        apiJson<{ users: AdminUser[] }>("/api/admin/users"),
        apiJson<{ clients: AdminClient[] }>("/api/admin/clients"),
        apiJson<{ users: BlulogAccessUser[] }>("/api/admin/blulog-access"),
      ]);
      setUsers(usersResponse.users);
      setClients(clientsResponse.clients);
      setBlulogUsers(blulogResponse.users);

      const wantedUserId = preferredUserId ?? selectedUserId;
      const nextUserId = usersResponse.users.some((item) => item.id === wantedUserId)
        ? wantedUserId
        : usersResponse.users[0]?.id ?? null;
      setSelectedUserId(nextUserId);

      const nextClientId = clientsResponse.clients.some((item) => item.id === selectedClientId)
        ? selectedClientId
        : clientsResponse.clients[0]?.id ?? null;
      setSelectedClientId(nextClientId);

      if (refresh && nextUserId === selectedUserId && nextUserId) {
        await loadUserAccess(nextUserId);
      }
      if (refresh) setMessage("Données actualisées.");
    } catch (loadError) {
      setError(readError(loadError));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }

  async function loadUserAccess(userId: string) {
    setIsAccessLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await apiJson<AccessResponse>(`/api/admin/users/${encodeURIComponent(userId)}/access`);
      setAccess(response);
      setSavedAccess(response);

      const currentClientExists = response.datasetAccess.some((dataset) => dataset.client_id === selectedClientId);
      if (!currentClientExists) {
        setSelectedClientId(response.datasetAccess[0]?.client_id ?? clients[0]?.id ?? null);
      }
    } catch (loadError) {
      setAccess(null);
      setSavedAccess(null);
      setError(readError(loadError));
    } finally {
      setIsAccessLoading(false);
    }
  }

  function selectUser(userId: string) {
    if (dirtyDatasetKeys.size > 0 && userId !== selectedUserId) {
      const confirmed = window.confirm("Les modifications non enregistrées seront perdues. Continuer ?");
      if (!confirmed) return;
    }
    setSelectedKeycloakUserId(null);
    setSelectedUserId(userId);
  }

  function selectDirectoryUser(user: AdminUserSummary) {
    if (dirtyDatasetKeys.size > 0 && user.localUserId !== selectedUserId) {
      const confirmed = window.confirm("Les modifications non enregistrées seront perdues. Continuer ?");
      if (!confirmed) return;
    }
    setSelectedKeycloakUserId(user.keycloakId);
    setSelectedUserId(user.localUserId);
  }

  async function handleCreateUser(input: CreateAdminUserInput) {
    const result = await adminUsers.createUser(input);
    await selectActionResult(result);
    return result;
  }

  async function handleProvisionUser(user: AdminUserSummary) {
    try {
      const result = await adminUsers.provisionUser(user);
      await selectActionResult(result);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Le provisionnement a échoué.");
    }
  }

  async function handleResendInvitation(user: AdminUserSummary) {
    try {
      const result = await adminUsers.resendInvitation(user);
      setMessage(result.message);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Le renvoi de l’invitation a échoué.");
    }
  }

  async function handleResetTemporaryPassword(user: AdminUserSummary, temporaryPassword: string) {
    const result = await adminUsers.resetTemporaryPassword(user, temporaryPassword);
    setMessage(result.message);
    setPasswordResetUser(null);
  }

  async function handleStatusChange(user: AdminUserSummary, enabled: boolean) {
    try {
      const result = await adminUsers.setUserEnabled(user, enabled);
      setMessage(result.message);
      setStatusChange(null);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Le changement d’état a échoué.");
    }
  }

  async function handleDeleteUser(user: AdminUserSummary) {
    try {
      const result = await adminUsers.deleteUser(user);
      setDeletionUser(null);
      if (!result.deleted) {
        setMessage(result.message);
        return;
      }
      setSelectedKeycloakUserId(null);
      setSelectedUserId(null);
      setAccess(null);
      setSavedAccess(null);
      await loadAdminData(true);
      setMessage(result.message);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "La suppression a échoué.");
    }
  }

  async function selectActionResult(result: AdminUserActionResult) {
    setSelectedKeycloakUserId(result.user.keycloakId);
    setSelectedUserId(result.user.localUserId);
    setError(null);
    if (result.user.localUserId) {
      await loadAdminData(true, result.user.localUserId);
    }
    setMessage(result.message);
  }

  function updateDatasetRole(datasetKey: string, role: DatasetRole) {
    if (!access) return;

    const datasetAccess = access.datasetAccess.map((dataset) => {
      if (dataset.dataset_key !== datasetKey) return dataset;
      return setDatasetRole(dataset, role);
    });
    setAccess({ ...access, datasetAccess });
    setMessage(null);
  }

  function giveReadAccessToAll() {
    if (!access || !selectedClientId) return;
    const datasetAccess = access.datasetAccess.map((dataset) => {
      if (dataset.client_id !== selectedClientId) return dataset;
      return {
        ...dataset,
        can_view: true,
        can_admin: false,
      };
    });
    setAccess({ ...access, datasetAccess });
    setMessage(null);
  }

  async function saveDataset(dataset: DatasetAccess) {
    if (!selectedUserId) return false;
    setSavingKeys((current) => new Set(current).add(dataset.dataset_key));
    setError(null);

    try {
      await apiJson(`/api/admin/users/${encodeURIComponent(selectedUserId)}/dataset-access`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(datasetPermissionPayload(dataset)),
      });
      markDatasetSaved(dataset);
      return true;
    } catch (saveError) {
      setError(readError(saveError));
      return false;
    } finally {
      setSavingKeys((current) => withoutKey(current, dataset.dataset_key));
    }
  }

  async function saveAllDatasets() {
    if (dirtySelectedDatasets.length === 0) return;
    setMessage(null);
    const results = await Promise.all(dirtySelectedDatasets.map(saveDataset));
    const savedCount = results.filter(Boolean).length;
    if (savedCount === dirtySelectedDatasets.length) {
      setMessage(`${savedCount} dataset${savedCount > 1 ? "s" : ""} enregistré${savedCount > 1 ? "s" : ""}.`);
    }
  }

  function markDatasetSaved(dataset: DatasetAccess) {
    setAccess((current) => replaceDataset(current, dataset));
    setSavedAccess((current) => replaceDataset(current, dataset));
  }

  async function saveClientAccess(clientId: string, role: "viewer" | "admin") {
    if (!selectedUserId) return;
    setError(null);

    try {
      await apiJson(`/api/admin/users/${encodeURIComponent(selectedUserId)}/client-access`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, role }),
      });
      updateClientAccess(clientId, role);
      setMessage(`Accès client ${role === "admin" ? "administrateur" : "lecture"} enregistré.`);
    } catch (saveError) {
      setError(readError(saveError));
    }
  }

  async function removeClientAccess(clientId: string) {
    if (!selectedUserId) return;
    setError(null);

    try {
      await apiJson(
        `/api/admin/users/${encodeURIComponent(selectedUserId)}/client-access/${encodeURIComponent(clientId)}`,
        { method: "DELETE" },
      );
      setAccess((current) => removeClientFromAccess(current, clientId));
      setSavedAccess((current) => removeClientFromAccess(current, clientId));
      setMessage("Accès client retiré.");
    } catch (removeError) {
      setError(readError(removeError));
    }
  }

  function updateClientAccess(clientId: string, role: "viewer" | "admin") {
    const client = clients.find((item) => item.id === clientId);
    if (!client) return;

    const nextAccess: ClientAccess = { client_id: clientId, client_name: client.name, role };
    setAccess((current) => replaceClientAccess(current, nextAccess));
    setSavedAccess((current) => replaceClientAccess(current, nextAccess));
  }

  async function updateBlulogAccess(userId: string, canViewBlulog: boolean) {
    setSavingBlulogUsers((current) => new Set(current).add(userId));
    setError(null);
    setMessage(null);

    try {
      await apiJson(`/api/admin/users/${encodeURIComponent(userId)}/blulog-access`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ can_view_blulog: canViewBlulog }),
      });
      setBlulogUsers((current) => current.map((item) => {
        if (item.id !== userId) return item;
        return { ...item, can_view_blulog: canViewBlulog };
      }));
      setMessage(canViewBlulog ? "Accès BluLog accordé." : "Accès BluLog retiré.");
    } catch (saveError) {
      setError(readError(saveError));
    } finally {
      setSavingBlulogUsers((current) => withoutKey(current, userId));
    }
  }

  return (
    <LandingShell
      active="admin"
      breadcrumb="Administration des accès"
      searchPlaceholder="Rechercher..."
      contentClassName="admin-app-content"
    >
      <section className="admin-access-page">
        <header className="admin-access-header">
          <div>
            <div className="admin-access-title-row">
              <h1>Administration des accès</h1>
              <span className="admin-role-badge"><ShieldCheck size={15} /> Admin global</span>
            </div>
            <p>Gérez les accès Lecture/Admin aux datasets et les autorisations globales BluLog.</p>
          </div>
          <div className="admin-header-actions">
            <button className="admin-button is-primary" type="button" onClick={() => setIsCreateOpen(true)}>
              <UserPlus size={16} /> Créer un utilisateur
            </button>
            <button className="admin-button is-secondary" type="button" onClick={() => { void loadAdminData(true); void adminUsers.refresh(); }} disabled={isRefreshing}>
              <RefreshCw size={16} className={isRefreshing ? "is-spinning" : ""} />
              {isRefreshing ? "Actualisation…" : "Actualiser"}
            </button>
          </div>
        </header>

        {error ? <Feedback type="error" message={error} actionLabel={error.includes("session") ? "Se reconnecter" : undefined} onAction={error.includes("session") ? login : undefined} /> : null}
        {message ? <Feedback type="success" message={message} /> : null}

        <div className="admin-view-tabs" role="tablist" aria-label="Sections d'administration">
          <button
            type="button"
            role="tab"
            aria-selected={activeView === "datasets"}
            className={activeView === "datasets" ? "is-active" : ""}
            onClick={() => setActiveView("datasets")}
          >
            Accès datasets
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeView === "blulog"}
            className={activeView === "blulog" ? "is-active" : ""}
            onClick={() => setActiveView("blulog")}
          >
            Accès BluLog
          </button>
        </div>

        <p className="admin-access-note">
          Cet écran modifie uniquement les droits utilisateurs. Il ne permet de supprimer aucun client ni dataset.
        </p>

        {isLoading ? (
          <div className="admin-access-loading"><span className="admin-spinner" />Chargement de l’administration…</div>
        ) : activeView === "datasets" ? (
          <div className="admin-access-grid">
            <aside className="admin-access-panel admin-users-panel">
              <PanelHeading icon={<Users size={18} />} title="Annuaire" count={adminUsers.users.length} />
              <SearchField value={adminUsers.query} onChange={adminUsers.setQuery} placeholder="Rechercher dans Keycloak" />
              {adminUsers.error ? (
                <div className="admin-directory-error" role="alert">
                  <p>{adminUsers.error}</p>
                  <div>
                    <button type="button" className="admin-button is-secondary" onClick={() => void adminUsers.refresh()}>Réessayer</button>
                    {adminUsers.errorStatus === 401 ? <button type="button" className="admin-button is-secondary" onClick={() => void login()}>Se reconnecter</button> : null}
                  </div>
                </div>
              ) : null}
              <div className="admin-compact-list">
                {adminUsers.isLoading ? <div className="admin-access-loading is-compact"><span className="admin-spinner" />Recherche dans l’annuaire…</div> : null}
                {!adminUsers.isLoading ? adminUsers.users.map((item) => (
                  <DirectoryUserRow
                    key={item.keycloakId}
                    user={item}
                    selected={selectedKeycloakUserId === item.keycloakId || Boolean(item.localUserId && selectedUserId === item.localUserId)}
                    pending={adminUsers.pendingUserIds.has(item.keycloakId)}
                    onSelect={selectDirectoryUser}
                    onProvision={handleProvisionUser}
                    onResend={handleResendInvitation}
                  />
                )) : null}
                {!adminUsers.isLoading && !adminUsers.error && adminUsers.users.length === 0 ? <EmptyState message="Aucun utilisateur Keycloak trouvé." /> : null}
                {adminUsers.error ? (
                  <div className="admin-local-fallback">
                    <h3>Profils locaux disponibles</h3>
                    {visibleLocalUsers.map((item) => (
                      <button key={item.id} type="button" className={selectedUserId === item.id ? "admin-user-row is-selected" : "admin-user-row"} onClick={() => selectUser(item.id)}>
                        <span className="admin-user-avatar">{userInitials(item)}</span>
                        <span className="admin-row-copy"><strong>{userDisplayName(item)}</strong><small>{item.email ?? "Profil local"}</small></span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <DirectoryPagination first={adminUsers.first} pageSize={adminUsers.pageSize} hasNextPage={adminUsers.hasNextPage} disabled={adminUsers.isLoading} onPrevious={adminUsers.previousPage} onNext={adminUsers.nextPage} />
            </aside>

            <section className="admin-access-panel admin-clients-panel">
              <PanelHeading icon={<Building2 size={18} />} title="Clients" count={clients.length} />
              <SearchField value={clientFilter} onChange={setClientFilter} placeholder="Rechercher client ou dataset" />
              <div className="admin-compact-list">
                {visibleClients.map((client) => {
                  const current = access?.clientAccess.find((item) => item.client_id === client.id);
                  const datasetCount = access?.datasetAccess.filter((dataset) => dataset.client_id === client.id).length ?? 0;
                  return (
                    <article
                      key={client.id}
                      className={selectedClientId === client.id ? "admin-client-item is-selected" : "admin-client-item"}
                    >
                      <button className="admin-client-select" type="button" onClick={() => setSelectedClientId(client.id)}>
                        <span className="admin-row-copy">
                          <strong>{client.name}</strong>
                          <small>{datasetCount} dataset{datasetCount > 1 ? "s" : ""}</small>
                        </span>
                        <AccessBadge role={current?.role} />
                      </button>
                      <div className="admin-client-actions" aria-disabled={!canEditAcl}>
                        <button type="button" className={!current ? "is-active" : ""} disabled={!canEditAcl || !current} onClick={() => removeClientAccess(client.id)}>Aucun</button>
                        <button type="button" className={current?.role === "viewer" ? "is-active" : ""} disabled={!canEditAcl} onClick={() => saveClientAccess(client.id, "viewer")}>Lecture</button>
                        <button type="button" className={current?.role === "admin" ? "is-active" : ""} disabled={!canEditAcl} onClick={() => saveClientAccess(client.id, "admin")}>Admin</button>
                      </div>
                    </article>
                  );
                })}
                {visibleClients.length === 0 ? <EmptyState message="Aucun client trouvé." /> : null}
              </div>
            </section>

            <section className="admin-access-panel admin-datasets-panel">
              {selectedIdentity ? (
                <SelectedIdentity
                  user={selectedIdentity}
                  pending={adminUsers.pendingUserIds.has(selectedIdentity.keycloakId)}
                  onProvision={handleProvisionUser}
                  onResend={handleResendInvitation}
                  onResetPassword={setPasswordResetUser}
                  onChangeStatus={(selected, enabled) => setStatusChange({ user: selected, enabled })}
                  onDelete={setDeletionUser}
                />
              ) : null}
              <div className="admin-datasets-heading">
                <div>
                  <span className="admin-eyebrow"><Database size={15} /> Accès datasets</span>
                  <h2>{selectedClient?.name ?? "Sélectionnez un client"}</h2>
                  <p>{selectedUser ? `Droits de ${userDisplayName(selectedUser)}` : "Sélectionnez un utilisateur"}</p>
                </div>
                {dirtySelectedDatasets.length > 0 ? <span className="admin-unsaved-badge">{dirtySelectedDatasets.length} non sauvegardé{dirtySelectedDatasets.length > 1 ? "s" : ""}</span> : null}
              </div>

              {selectedClient ? (
                <div className="admin-quick-actions">
                  <button type="button" className="admin-button is-secondary" onClick={giveReadAccessToAll} disabled={!canEditAcl || selectedDatasets.length === 0}>Lecture pour tous</button>
                  <button type="button" className="admin-button is-primary" onClick={saveAllDatasets} disabled={!canEditAcl || dirtySelectedDatasets.length === 0 || savingKeys.size > 0}>
                    <Save size={15} /> Tout sauvegarder
                  </button>
                </div>
              ) : null}

              {isAccessLoading ? (
                <div className="admin-access-loading is-compact"><span className="admin-spinner" />Chargement des permissions…</div>
              ) : selectedDatasets.length > 0 ? (
                <div className="admin-dataset-table-wrap">
                  <table className="admin-dataset-table">
                    <thead>
                      <tr>
                        <th>Dataset</th>
                        <th>Accès</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDatasets.map((dataset) => {
                        const isDirty = dirtyDatasetKeys.has(dataset.dataset_key);
                        return (
                          <tr key={dataset.dataset_key} className={isDirty ? "is-dirty" : ""}>
                            <td data-label="Dataset">
                              <strong>{dataset.dataset_name ?? dataset.dataset_key}</strong>
                              <small>{dataset.dataset_key}</small>
                              {isDirty ? <span className="admin-row-modified">Modifié</span> : null}
                            </td>
                            <td data-label="Accès">
                              <DatasetRolePicker
                                dataset={dataset}
                                inheritedRole={access?.clientAccess.find((item) => item.client_id === dataset.client_id)?.role}
                                disabled={!canEditAcl}
                                onChange={(role) => updateDatasetRole(dataset.dataset_key, role)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState message={selectedClient ? "Aucun dataset disponible pour ce client." : "Sélectionnez un client pour gérer ses datasets."} />
              )}
            </section>
          </div>
        ) : (
          <BlulogAccessPanel
            users={blulogUsers}
            filter={userFilter}
            onFilterChange={setUserFilter}
            savingUsers={savingBlulogUsers}
            onChange={updateBlulogAccess}
          />
        )}
      </section>
      {isCreateOpen ? (
        <CreateUserDialog
          isSubmitting={adminUsers.isCreating}
          onClose={() => setIsCreateOpen(false)}
          onCreate={handleCreateUser}
          onSearchExisting={(email) => { adminUsers.setQuery(email); setIsCreateOpen(false); }}
          onReconnect={login}
        />
      ) : null}
      {passwordResetUser ? (
        <TemporaryPasswordDialog
          user={passwordResetUser}
          isSubmitting={adminUsers.pendingUserIds.has(passwordResetUser.keycloakId)}
          onClose={() => setPasswordResetUser(null)}
          onSubmit={handleResetTemporaryPassword}
        />
      ) : null}
      {statusChange ? (
        <StatusConfirmationDialog
          user={statusChange.user}
          enabled={statusChange.enabled}
          isSubmitting={adminUsers.pendingUserIds.has(statusChange.user.keycloakId)}
          onClose={() => setStatusChange(null)}
          onConfirm={handleStatusChange}
        />
      ) : null}
      {deletionUser ? (
        <DeleteUserDialog
          user={deletionUser}
          isSubmitting={adminUsers.pendingUserIds.has(deletionUser.keycloakId)}
          onClose={() => setDeletionUser(null)}
          onConfirm={handleDeleteUser}
        />
      ) : null}
    </LandingShell>
  );
}

function DirectoryUserRow(props: {
  user: AdminUserSummary;
  selected: boolean;
  pending: boolean;
  onSelect: (user: AdminUserSummary) => void;
  onProvision: (user: AdminUserSummary) => void;
  onResend: (user: AdminUserSummary) => void;
}) {
  const user = props.user;
  return (
    <article className={props.selected ? "admin-directory-user is-selected" : "admin-directory-user"}>
      <button type="button" className="admin-user-row" onClick={() => props.onSelect(user)}>
        <span className="admin-user-avatar">{directoryUserInitials(user)}</span>
        <span className="admin-row-copy">
          <strong>{user.displayName}</strong>
          <small>{user.email ?? user.username ?? "Adresse email indisponible"}</small>
          <span className="admin-user-statuses">
            <IdentityStatusBadges user={user} />
          </span>
        </span>
      </button>
      {user.provisioningStatus === "not_provisioned" ? (
        <button type="button" className="admin-directory-action" disabled={props.pending} onClick={() => props.onProvision(user)}>
          {props.pending ? "Reprise…" : "Reprendre le provisionnement"}
        </button>
      ) : null}
      {user.operationStatus === "invitation_failed" && user.enabled ? (
        <button type="button" className="admin-directory-action" disabled={props.pending} onClick={() => props.onResend(user)}>
          {props.pending ? "Envoi…" : "Renvoyer l’invitation"}
        </button>
      ) : null}
    </article>
  );
}

function IdentityStatusBadges({ user }: { user: AdminUserSummary }) {
  return (
    <>
      <span className={user.enabled ? "admin-identity-badge is-enabled" : "admin-identity-badge is-disabled"}>
        {user.enabled ? "Actif" : "Désactivé"}
      </span>
      <span className={user.provisioningStatus === "provisioned" ? "admin-identity-badge is-provisioned" : "admin-identity-badge is-warning"}>
        {user.provisioningStatus === "provisioned" ? "Provisionné" : "Provisionnement à reprendre"}
      </span>
      {user.operationStatus === "invitation_failed" ? <span className="admin-identity-badge is-warning">Invitation échouée</span> : null}
      {user.operationStatus === "invitation_sent" ? <span className="admin-identity-badge is-provisioned">Invitation envoyée</span> : null}
    </>
  );
}

function SelectedIdentity(props: {
  user: AdminUserSummary;
  pending: boolean;
  onProvision: (user: AdminUserSummary) => void;
  onResend: (user: AdminUserSummary) => void;
  onResetPassword: (user: AdminUserSummary) => void;
  onChangeStatus: (user: AdminUserSummary, enabled: boolean) => void;
  onDelete: (user: AdminUserSummary) => void;
}) {
  const user = props.user;
  return (
    <section className="admin-account-toolbar" aria-label="Gestion du compte">
      <div className="admin-account-buttons">
        {user.provisioningStatus === "not_provisioned" ? (
          <button type="button" className="admin-button is-primary" disabled={props.pending} onClick={() => props.onProvision(user)}>
            {props.pending ? "Provisionnement…" : "Reprendre le provisionnement"}
          </button>
        ) : null}
        {user.operationStatus === "invitation_failed" && user.enabled ? (
          <button type="button" className="admin-button is-primary" disabled={props.pending} onClick={() => props.onResend(user)}>
            {props.pending ? "Envoi…" : "Renvoyer l’invitation"}
          </button>
        ) : null}
        {user.operationStatus === "temporary_password_failed" ? (
          <button type="button" className="admin-button is-primary" disabled={props.pending} onClick={() => props.onResetPassword(user)}>
            <KeyRound size={15} /> Définir un nouveau mot de passe temporaire
          </button>
        ) : null}
        <button
          type="button"
          className="admin-button is-secondary"
          disabled={props.pending || user.protected}
          onClick={() => props.onChangeStatus(user, !user.enabled)}
        >
          {user.enabled ? <UserX size={15} /> : <UserCheck size={15} />}
          {user.enabled ? "Désactiver le compte" : "Réactiver le compte"}
        </button>
        <button
          type="button"
          className="admin-button is-danger"
          disabled={props.pending || user.protected}
          onClick={() => props.onDelete(user)}
        >
          <Trash2 size={15} /> Supprimer définitivement l’utilisateur
        </button>
      </div>
      {user.provisioningStatus === "not_provisioned" ? (
        <p className="admin-identity-explanation">Identité locale introuvable. Les contrôles ACL restent désactivés jusqu’à la fin du provisionnement.</p>
      ) : null}
      {!user.enabled ? <p className="admin-identity-explanation">Ce compte est désactivé. Ses accès restent consultables.</p> : null}
      {user.protected ? <p className="admin-protected-explanation">Ce compte administrateur ou compte courant est protégé.</p> : null}
      {user.operationStatus === "deletion_failed" ? <p className="admin-identity-explanation">La suppression locale est terminée. Relancez la suppression pour retirer l’identité Keycloak désactivée.</p> : null}
    </section>
  );
}

function DirectoryPagination(props: {
  first: number;
  pageSize: number;
  hasNextPage: boolean;
  disabled: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const page = Math.floor(props.first / props.pageSize) + 1;
  return (
    <nav className="admin-directory-pagination" aria-label="Pagination de l’annuaire">
      <button type="button" disabled={props.disabled || props.first === 0} onClick={props.onPrevious}>Précédent</button>
      <span>Page {page}</span>
      <button type="button" disabled={props.disabled || !props.hasNextPage} onClick={props.onNext}>Suivant</button>
    </nav>
  );
}

function CreateUserDialog(props: {
  isSubmitting: boolean;
  onClose: () => void;
  onCreate: (input: CreateAdminUserInput) => Promise<AdminUserActionResult>;
  onSearchExisting: (query: string) => void;
  onReconnect: () => Promise<void>;
}) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [credentialMode, setCredentialMode] = useState<"invitation" | "temporary_password">("invitation");
  const [temporaryPassword, setTemporaryPasswordValue] = useState("");
  const [temporaryPasswordConfirmation, setTemporaryPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const passwordConfirmationRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const busyRef = useRef(false);
  const onCloseRef = useRef(props.onClose);
  busyRef.current = props.isSubmitting || isSubmitted;
  onCloseRef.current = props.onClose;

  useEffect(() => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    usernameRef.current?.focus();

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (busyRef.current) return;
      onCloseRef.current();
    }

    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      returnFocusRef.current?.focus();
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (props.isSubmitting || isSubmitted) return;

    const input: CreateAdminUserInput = { username: username.trim() };
    const trimmedEmail = email.trim();
    if (trimmedEmail) input.email = trimmedEmail;
    if (credentialMode === "temporary_password") input.temporaryPassword = temporaryPassword;
    const nextErrors = validateCreateUser(input);
    if (credentialMode === "temporary_password" && temporaryPassword !== temporaryPasswordConfirmation) {
      nextErrors.temporaryPasswordConfirmation = "La confirmation doit être identique au mot de passe.";
    }
    setErrors(nextErrors);
    setSubmitError(null);
    if (Object.keys(nextErrors).length > 0) {
      focusFirstCreateError(nextErrors, usernameRef, emailRef, passwordRef, passwordConfirmationRef);
      return;
    }

    setIsSubmitted(true);
    try {
      await props.onCreate(input);
      props.onClose();
    } catch (createError) {
      setSubmitError(createError instanceof Error ? createError.message : "La création a échoué.");
      setIsSubmitted(false);
    }
  }

  const conflict = submitError === "Cette adresse ou ce compte existe déjà.";
  return (
    <div className="admin-dialog-backdrop">
      <section className="admin-create-dialog" role="dialog" aria-modal="true" aria-labelledby="create-user-title">
        <header>
          <div>
            <h2 id="create-user-title">Créer un utilisateur</h2>
            <p>L’email est facultatif. S’il est renseigné, Keycloak enverra le lien permettant de définir le mot de passe.</p>
          </div>
          <button type="button" className="admin-dialog-close" aria-label="Fermer la création d’utilisateur" disabled={props.isSubmitting || isSubmitted} onClick={props.onClose}><X size={18} /></button>
        </header>
        <form onSubmit={submit} noValidate>
          {submitError ? <div className="admin-form-error" role="alert">{submitError}</div> : null}
          {conflict ? <button type="button" className="admin-button is-secondary" onClick={() => props.onSearchExisting(email.trim() || username.trim())}>Rechercher l’utilisateur existant</button> : null}
          {submitError?.includes("session a expiré") ? <button type="button" className="admin-button is-secondary" onClick={() => void props.onReconnect()}>Se reconnecter</button> : null}
          <label>
            Nom d’utilisateur
            <input ref={usernameRef} name="username" value={username} maxLength={254} autoComplete="username" aria-invalid={Boolean(errors.username)} aria-describedby={errors.username ? "username-error" : undefined} onChange={(event) => setUsername(event.target.value)} />
            {errors.username ? <small id="username-error" className="admin-field-error">{errors.username}</small> : null}
          </label>
          <label>
            Email (facultatif)
            <input ref={emailRef} name="email" type="email" value={email} maxLength={254} autoComplete="email" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "email-error" : undefined} onChange={(event) => setEmail(event.target.value)} />
            {errors.email ? <small id="email-error" className="admin-field-error">{errors.email}</small> : null}
          </label>
          <fieldset className="admin-credential-mode">
            <legend>Mode d’accès initial</legend>
            <label>
              <input type="radio" name="credentialMode" checked={credentialMode === "invitation"} onChange={() => setCredentialMode("invitation")} />
              Invitation par email (recommandé)
            </label>
            <label>
              <input type="radio" name="credentialMode" checked={credentialMode === "temporary_password"} onChange={() => setCredentialMode("temporary_password")} />
              Mot de passe temporaire
            </label>
          </fieldset>
          {credentialMode === "temporary_password" ? (
            <div className="admin-temporary-password-fields">
              <label>
                Mot de passe temporaire
                <span className="admin-password-input">
                  <input ref={passwordRef} name="temporaryPassword" type={showPassword ? "text" : "password"} value={temporaryPassword} maxLength={128} autoComplete="new-password" aria-invalid={Boolean(errors.temporaryPassword)} aria-describedby={errors.temporaryPassword ? "temporary-password-error" : undefined} onChange={(event) => setTemporaryPasswordValue(event.target.value)} />
                  <button type="button" aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"} onClick={() => setShowPassword((current) => !current)}>{showPassword ? <EyeOff size={17} /> : <Eye size={17} />}</button>
                </span>
                {errors.temporaryPassword ? <small id="temporary-password-error" className="admin-field-error">{errors.temporaryPassword}</small> : null}
              </label>
              <label>
                Confirmation du mot de passe
                <input ref={passwordConfirmationRef} name="temporaryPasswordConfirmation" type={showPassword ? "text" : "password"} value={temporaryPasswordConfirmation} maxLength={128} autoComplete="new-password" aria-invalid={Boolean(errors.temporaryPasswordConfirmation)} aria-describedby={errors.temporaryPasswordConfirmation ? "temporary-password-confirmation-error" : undefined} onChange={(event) => setTemporaryPasswordConfirmation(event.target.value)} />
                {errors.temporaryPasswordConfirmation ? <small id="temporary-password-confirmation-error" className="admin-field-error">{errors.temporaryPasswordConfirmation}</small> : null}
              </label>
              <p className="admin-password-help">L’utilisateur devra modifier ce mot de passe lors de sa première connexion. Transmettez-le uniquement par un canal sécurisé.</p>
            </div>
          ) : null}
          <footer>
            <button type="button" className="admin-button is-secondary" disabled={props.isSubmitting || isSubmitted} onClick={props.onClose}>Annuler</button>
            <button type="submit" className="admin-button is-primary" disabled={props.isSubmitting || isSubmitted}>{props.isSubmitting || isSubmitted ? "Création…" : "Créer l’utilisateur"}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function validateCreateUser(input: CreateAdminUserInput) {
  const errors: Record<string, string> = {};
  if (!input.username) errors.username = "Le nom d’utilisateur est obligatoire.";
  if (input.username.length > 254) errors.username = "Le nom d’utilisateur ne doit pas dépasser 254 caractères.";
  if (input.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email)) errors.email = "Saisissez une adresse email valide.";
  if (input.email && input.email.length > 254) errors.email = "L’email ne doit pas dépasser 254 caractères.";
  if (input.temporaryPassword !== undefined && input.temporaryPassword.length < 8) errors.temporaryPassword = "Le mot de passe temporaire doit contenir au moins 8 caractères.";
  if (input.temporaryPassword && input.temporaryPassword.length > 128) errors.temporaryPassword = "Le mot de passe temporaire ne doit pas dépasser 128 caractères.";
  return errors;
}

function TemporaryPasswordDialog(props: {
  user: AdminUserSummary;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (user: AdminUserSummary, temporaryPassword: string) => Promise<void>;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password.length < 8) {
      setError("Le mot de passe temporaire doit contenir au moins 8 caractères.");
      return;
    }
    if (password.length > 128) {
      setError("Le mot de passe temporaire ne doit pas dépasser 128 caractères.");
      return;
    }
    if (password !== confirmation) {
      setError("La confirmation doit être identique au mot de passe.");
      return;
    }
    setError(null);
    try {
      await props.onSubmit(props.user, password);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Le mot de passe temporaire n’a pas pu être configuré.");
    }
  }

  return (
    <div className="admin-dialog-backdrop">
      <section className="admin-create-dialog" role="dialog" aria-modal="true" aria-labelledby="reset-password-title">
        <header>
          <div><h2 id="reset-password-title">Définir un mot de passe temporaire</h2><p>Pour {props.user.displayName}. Il devra être modifié lors de la première connexion.</p></div>
          <button type="button" className="admin-dialog-close" aria-label="Fermer" disabled={props.isSubmitting} onClick={props.onClose}><X size={18} /></button>
        </header>
        <form onSubmit={submit} noValidate>
          {error ? <div className="admin-form-error" role="alert">{error}</div> : null}
          <label>Mot de passe temporaire<span className="admin-password-input"><input type={showPassword ? "text" : "password"} value={password} autoComplete="new-password" maxLength={128} onChange={(event) => setPassword(event.target.value)} /><button type="button" aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"} onClick={() => setShowPassword((current) => !current)}>{showPassword ? <EyeOff size={17} /> : <Eye size={17} />}</button></span></label>
          <label>Confirmation du mot de passe<input type={showPassword ? "text" : "password"} value={confirmation} autoComplete="new-password" maxLength={128} onChange={(event) => setConfirmation(event.target.value)} /></label>
          <p className="admin-password-help">Ne transmettez ce mot de passe que par un canal sécurisé.</p>
          <footer><button type="button" className="admin-button is-secondary" disabled={props.isSubmitting} onClick={props.onClose}>Annuler</button><button type="submit" className="admin-button is-primary" disabled={props.isSubmitting}>{props.isSubmitting ? "Configuration…" : "Configurer"}</button></footer>
        </form>
      </section>
    </div>
  );
}

function StatusConfirmationDialog(props: {
  user: AdminUserSummary;
  enabled: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (user: AdminUserSummary, enabled: boolean) => Promise<void>;
}) {
  return (
    <div className="admin-dialog-backdrop">
      <section className="admin-create-dialog" role="dialog" aria-modal="true" aria-labelledby="status-confirmation-title">
        <header><div><h2 id="status-confirmation-title">{props.enabled ? "Réactiver le compte" : "Désactiver le compte"}</h2><p>{props.enabled ? "L’utilisateur pourra de nouveau se connecter." : "Cet utilisateur ne pourra plus se connecter. Ses accès Datavis seront conservés."}</p></div><button type="button" className="admin-dialog-close" aria-label="Fermer" disabled={props.isSubmitting} onClick={props.onClose}><X size={18} /></button></header>
        <footer className="admin-dialog-footer"><button type="button" className="admin-button is-secondary" disabled={props.isSubmitting} onClick={props.onClose}>Annuler</button><button type="button" className="admin-button is-primary" disabled={props.isSubmitting} onClick={() => void props.onConfirm(props.user, props.enabled)}>{props.isSubmitting ? "Modification…" : "Confirmer"}</button></footer>
      </section>
    </div>
  );
}

function DeleteUserDialog(props: {
  user: AdminUserSummary;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (user: AdminUserSummary) => Promise<void>;
}) {
  const [confirmation, setConfirmation] = useState("");
  const canDelete = confirmation === "SUPPRIMER";
  return (
    <div className="admin-dialog-backdrop">
      <section className="admin-create-dialog admin-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-user-title">
        <header><div><h2 id="delete-user-title">Supprimer définitivement l’utilisateur</h2><p>Cette opération est irréversible.</p></div><button type="button" className="admin-dialog-close" aria-label="Fermer" disabled={props.isSubmitting} onClick={props.onClose}><X size={18} /></button></header>
        <div className="admin-delete-warning" role="note">L’identité Keycloak, le profil Datavis, les accès clients, les ACL datasets et l’accès BluLog seront supprimés.</div>
        <label className="admin-confirmation-field">Saisissez <strong>SUPPRIMER</strong> pour confirmer<input value={confirmation} autoComplete="off" onChange={(event) => setConfirmation(event.target.value)} /></label>
        <footer className="admin-dialog-footer"><button type="button" className="admin-button is-secondary" disabled={props.isSubmitting} onClick={props.onClose}>Annuler</button><button type="button" className="admin-button is-danger" disabled={!canDelete || props.isSubmitting} onClick={() => void props.onConfirm(props.user)}>{props.isSubmitting ? "Suppression…" : "Supprimer définitivement"}</button></footer>
      </section>
    </div>
  );
}

function focusFirstCreateError(
  errors: Record<string, string>,
  usernameRef: { current: HTMLInputElement | null },
  emailRef: { current: HTMLInputElement | null },
  passwordRef: { current: HTMLInputElement | null },
  passwordConfirmationRef: { current: HTMLInputElement | null },
) {
  if (errors.username) {
    usernameRef.current?.focus();
    return;
  }
  if (errors.email) {
    emailRef.current?.focus();
    return;
  }
  if (errors.temporaryPassword) {
    passwordRef.current?.focus();
    return;
  }
  if (errors.temporaryPasswordConfirmation) {
    passwordConfirmationRef.current?.focus();
    return;
  }
  emailRef.current?.focus();
}

function PanelHeading({ icon, title, count }: { icon: ReactNode; title: string; count: number }) {
  return (
    <div className="admin-panel-heading">
      <span>{icon}</span>
      <h2>{title}</h2>
      <small>{count}</small>
    </div>
  );
}

function SearchField(props: { value: string; onChange: (value: string) => void; placeholder: string }) {
  return (
    <label className="admin-search-field">
      <Search size={16} aria-hidden="true" />
      <input value={props.value} onChange={(event) => props.onChange(event.target.value)} placeholder={props.placeholder} aria-label={props.placeholder} />
    </label>
  );
}

function AccessBadge({ role }: { role?: "viewer" | "admin" }) {
  if (role === "admin") return <span className="admin-access-badge is-admin">Admin</span>;
  if (role === "viewer") return <span className="admin-access-badge is-viewer">Lecture</span>;
  return <span className="admin-access-badge">Aucun accès</span>;
}

function DatasetRolePicker(props: {
  dataset: DatasetAccess;
  inheritedRole?: "viewer" | "admin";
  disabled: boolean;
  onChange: (role: DatasetRole) => void;
}) {
  const directRole = datasetRole(props.dataset);
  const role = directRole === "none" ? props.inheritedRole ?? "none" : directRole;
  let inheritedLabel: string | null = null;
  if (directRole === "none" && props.inheritedRole === "viewer") inheritedLabel = "Lecture héritée du client";
  if (directRole === "none" && props.inheritedRole === "admin") inheritedLabel = "Admin hérité du client";
  return (
    <div className="admin-dataset-role-control">
      <div className="admin-role-picker" aria-label={`Accès à ${props.dataset.dataset_name ?? props.dataset.dataset_key}`}>
        <button type="button" disabled={props.disabled || props.inheritedRole !== undefined} className={role === "none" ? "is-active" : ""} onClick={() => props.onChange("none")}>Aucun</button>
        <button type="button" disabled={props.disabled} className={role === "viewer" ? "is-active" : ""} onClick={() => props.onChange("viewer")}>Lecture</button>
        <button type="button" disabled={props.disabled} className={role === "admin" ? "is-active" : ""} onClick={() => props.onChange("admin")}>Admin</button>
      </div>
      {inheritedLabel ? <small className="admin-inherited-access">{inheritedLabel}</small> : null}
    </div>
  );
}

function BlulogAccessPanel(props: {
  users: BlulogAccessUser[];
  filter: string;
  onFilterChange: (value: string) => void;
  savingUsers: Set<string>;
  onChange: (userId: string, canView: boolean) => void;
}) {
  const filter = props.filter.trim().toLowerCase();
  const users = filter ? props.users.filter((user) => userLabel(user).toLowerCase().includes(filter)) : props.users;
  const allowedCount = props.users.filter((user) => user.can_view_blulog).length;

  return (
    <section className="admin-access-panel admin-blulog-panel">
      <div className="admin-datasets-heading">
        <div>
          <span className="admin-eyebrow"><ShieldCheck size={15} /> Accès global</span>
          <h2>Utilisateurs BluLog</h2>
          <p>{allowedCount} utilisateur{allowedCount > 1 ? "s" : ""} autorisé{allowedCount > 1 ? "s" : ""}</p>
        </div>
      </div>
      <SearchField value={props.filter} onChange={props.onFilterChange} placeholder="Rechercher un utilisateur" />
      <div className="admin-blulog-list">
        {users.map((user) => {
          const isSaving = props.savingUsers.has(user.id);
          return (
            <article className="admin-blulog-user" key={user.id}>
              <span className="admin-user-avatar">{userInitials(user)}</span>
              <span className="admin-row-copy">
                <strong>{userDisplayName(user)}</strong>
                <small>{user.email ?? user.keycloak_sub}</small>
              </span>
              <span className={user.can_view_blulog ? "admin-access-badge is-viewer" : "admin-access-badge"}>
                {user.can_view_blulog ? "Autorisé" : "Non autorisé"}
              </span>
              <button
                type="button"
                className={user.can_view_blulog ? "admin-button is-ghost-danger" : "admin-button is-primary"}
                disabled={isSaving}
                onClick={() => props.onChange(user.id, !user.can_view_blulog)}
              >
                {isSaving ? "Enregistrement…" : user.can_view_blulog ? "Retirer l’accès" : "Autoriser"}
              </button>
            </article>
          );
        })}
        {users.length === 0 ? <EmptyState message="Aucun utilisateur trouvé." /> : null}
      </div>
    </section>
  );
}

function Feedback(props: {
  type: "error" | "success";
  message: string;
  actionLabel?: string;
  onAction?: () => Promise<void>;
}) {
  return (
    <div className={`admin-access-feedback is-${props.type}`} role={props.type === "error" ? "alert" : "status"}>
      <ShieldCheck size={19} aria-hidden="true" />
      <span>{props.message}</span>
      {props.actionLabel && props.onAction ? <button type="button" onClick={() => void props.onAction?.()}>{props.actionLabel}</button> : null}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <div className="admin-empty-state">{message}</div>;
}

function datasetRole(dataset: DatasetAccess): DatasetRole {
  if (dataset.can_admin) return "admin";
  if (dataset.can_view) return "viewer";
  return "none";
}

function setDatasetRole(dataset: DatasetAccess, role: DatasetRole): DatasetAccess {
  if (role === "admin") return { ...dataset, can_view: true, can_admin: true };
  if (role === "viewer") return { ...dataset, can_view: true, can_admin: false };
  return resetDatasetPermissions(dataset);
}

function resetDatasetPermissions(dataset: DatasetAccess): DatasetAccess {
  return {
    ...dataset,
    can_view: false,
    can_admin: false,
  };
}

function datasetPermissionPayload(dataset: DatasetAccess) {
  return {
    dataset_key: dataset.dataset_key,
    can_view: dataset.can_view,
    can_admin: dataset.can_admin,
  };
}

function samePermissions(left: DatasetAccess, right: DatasetAccess) {
  return left.can_view === right.can_view && left.can_admin === right.can_admin;
}

function replaceDataset(current: AccessResponse | null, dataset: DatasetAccess) {
  if (!current) return current;
  return {
    ...current,
    datasetAccess: current.datasetAccess.map((item) => item.dataset_key === dataset.dataset_key ? dataset : item),
  };
}

function replaceClientAccess(current: AccessResponse | null, nextAccess: ClientAccess) {
  if (!current) return current;
  const remaining = current.clientAccess.filter((item) => item.client_id !== nextAccess.client_id);
  return { ...current, clientAccess: [...remaining, nextAccess] };
}

function removeClientFromAccess(current: AccessResponse | null, clientId: string) {
  if (!current) return current;
  return { ...current, clientAccess: current.clientAccess.filter((item) => item.client_id !== clientId) };
}

function withoutKey(keys: Set<string>, key: string) {
  const next = new Set(keys);
  next.delete(key);
  return next;
}

function userDisplayName(user: AdminUser) {
  return user.display_name ?? user.email ?? user.keycloak_sub;
}

function userLabel(user: AdminUser) {
  return `${userDisplayName(user)} ${user.email ?? ""} ${user.keycloak_sub}`;
}

function userInitials(user: AdminUser) {
  const label = userDisplayName(user).trim();
  return initialsFromLabel(label);
}

function directoryUserInitials(user: AdminUserSummary) {
  return initialsFromLabel(user.displayName);
}

function initialsFromLabel(label: string) {
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

function readError(error: unknown) {
  if (error instanceof ApiHttpError && error.status === 401) return "Erreur 401 : votre session a expiré. Reconnectez-vous.";
  if (error instanceof ApiHttpError && error.status === 403) return "Erreur 403 : accès administrateur requis.";
  if (error instanceof Error) return error.message;
  return "Une erreur inattendue est survenue.";
}
